import { Alert, AlertDescription, AlertTitle, Button, Text } from "@lagoon/ui";

import { ApiError } from "../api.js";

/**
 * What an owner sees when the platform refuses to publish for an unverified
 * address.
 *
 * Matched on `code`, never on the message text. The MCP server relays the
 * platform's sentence to Claude verbatim, so that sentence has to read as
 * standalone prose; if this screen matched on it, rewording it for Claude's
 * benefit would silently turn this notice back into a plain red box.
 *
 * Two surfaces reach this: creating a site, and asking for an upload URL.
 * Both are refused for the same reason and both offer the same remedy, so it
 * is one component rather than a sentence copied twice.
 */

/** True when the platform refused specifically for an unverified address. */
export function isEmailUnverified(caught: unknown): boolean {
  return caught instanceof ApiError && caught.code === "email_unverified";
}

export function VerifyEmailNotice(props: { supportEmail: string }): React.ReactElement {
  return (
    <Alert tone="danger">
      <AlertTitle>Verify your email address to publish</AlertTitle>
      <AlertDescription className="lg:flex lg:flex-col lg:gap-4">
        <span>
          Check your inbox for the confirmation link. Everything else in the dashboard keeps
          working; only creating a site and uploading a release are held back until the address is
          confirmed.
        </span>
        {/*
          Placeholder, and deliberately an inert one.

          Verification mail is sent by the identity provider, so resending it
          means asking the tenant to send it again, and nothing in this estate
          can do that yet: there is no platform route behind this button. That
          route is LGN-166, and wiring this button is the other half of
          LGN-170, which is why the half that could be built (the plan, trial
          and quota panel) shipped without it.

          A button that silently does nothing would be worse than no button, so
          it is disabled and says why. The address beside it is the remedy that
          does work today, and staff are not affected either way: they sign in
          through Entra, where the directory settled the address long before
          this platform saw it.
        */}
        <span className="lg:flex lg:flex-wrap lg:items-center lg:gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled
            title="Not available yet: see the note beside this button"
          >
            Resend verification email
          </Button>
          <Text as="span" size="caption" tone="subtle">
            Resending is not wired up yet. Ask{" "}
            <a href={`mailto:${props.supportEmail}`}>{props.supportEmail}</a> to confirm the address
            for you.
          </Text>
        </span>
      </AlertDescription>
    </Alert>
  );
}
